/**
 * Load Firebase Auth compat scripts (app + auth). Shared by auth-firebase facade.
 */

declare global {
  interface Window {
    __LECTURA_FIREBASE_CONFIG__?: unknown;
    firebase?: FirebaseCompat;
  }
}

export interface FirebaseCompat {
  app?(name?: string): { name: string };
  apps?: unknown[];
  initializeApp?(config: unknown): { name: string };
  functions?: (() => {
    httpsCallable(name: string): (data?: unknown) => Promise<{ data?: unknown }>;
  }) & {
    region(region: string): {
      httpsCallable(name: string): (data?: unknown) => Promise<{ data?: unknown }>;
    };
  };
  auth: (() => FirebaseAuthInstance) & {
    GoogleAuthProvider: new () => FirebaseAuthProvider;
    GithubAuthProvider?: new () => FirebaseAuthProvider;
  };
}

export interface FirebaseAuthInstance {
  onAuthStateChanged(cb: (user: FirebaseUser | null) => void): () => void;
  signInAnonymously(): Promise<unknown>;
  signInWithPopup(provider: unknown): Promise<{ user: FirebaseUser; credential?: unknown }>;
  signOut(): Promise<void>;
  currentUser: FirebaseUser | null;
}

export interface FirebaseUser {
  uid: string;
  isAnonymous: boolean;
  email?: string;
  displayName?: string;
  photoURL?: string;
  providerData?: Array<{ providerId?: string; email?: string }>;
  linkWithCredential?: (cred: unknown) => Promise<unknown>;
  reauthenticateWithPopup?: (provider: unknown) => Promise<unknown>;
}

export interface FirebaseAuthProvider {
  setCustomParameters?: (p: { login_hint?: string }) => void;
}

export interface LecturaUser {
  uid: string;
  isAnonymous: boolean;
  email?: string;
  displayName?: string;
  photoURL?: string;
}

const FIREBASE_VERSION = '10.7.1';
const BASE = `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/`;
const SCRIPTS = [
  BASE + 'firebase-app-compat.js',
  BASE + 'firebase-auth-compat.js',
  BASE + 'firebase-firestore-compat.js',
  BASE + 'firebase-storage-compat.js',
  BASE + 'firebase-functions-compat.js'
];

let loadPromise: Promise<void> | null = null;

export function loadFirebaseAuthScripts(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  const w = window as Window & { firebase?: unknown };
  if (w.firebase && typeof (w.firebase as { auth?: () => unknown }).auth === 'function') {
    return Promise.resolve();
  }
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    let i = 0;
    function next(): void {
      if (i >= SCRIPTS.length) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = SCRIPTS[i];
      script.onload = () => {
        i++;
        next();
      };
      script.onerror = () => reject(new Error('Failed to load ' + SCRIPTS[i]));
      document.head.appendChild(script);
    }
    next();
  });
  return loadPromise;
}

export function toLecturaUser(user: FirebaseUser | null): LecturaUser | null {
  if (!user) return null;
  const providerData = user.providerData?.map((p) => ({
    providerId: p?.providerId,
    email: p?.email
  }));
  return {
    uid: user.uid,
    isAnonymous: user.isAnonymous,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    ...(providerData?.length ? { providerData } : {})
  };
}
